import { FC, useEffect, useState } from 'react';
import { Alert } from '@/components/ui';
import { useWizardStatus } from '@/hooks/useWizard';

// Feature imports
import { TabType } from '../features/profile/types';
import { useProfile } from '../features/profile/hooks/useProfile';
import { ProfileSidebar } from '../features/profile/components/ProfileSidebar';
import { ProfileTab } from '../features/profile/components/ProfileTab';
import { EmailPasswordTab } from '../features/profile/components/EmailPasswordTab';
import { BillingTab } from '../features/profile/components/BillingTab';
import { SummarizerTab } from '../features/profile/components/SummarizerTab';

// Existing components
import { EmailConfigTab } from '@/components/email-config/EmailConfigTab';
import { WizardStepModal } from '@/components/profile/WizardStepModal';
import { WizardConfigView } from '@/components/profile/WizardConfigView';
import { WizardStepDetailView } from '@/components/profile/WizardStepDetailView';

// Re-export types if needed by other components, or just use them locally
// The TabType is imported from features/profile/types

const Profile: FC = () => {
  // Global Profile State
  const {
    profile,
    setProfile,
    isLoading,
    isEditing,
    isSaving,
    error,
    setError,
    success,
    setSuccess,
    formData,
    formErrors,
    handleEdit,
    handleCancel,
    handleSave,
    handleInputChange,
  } = useProfile();

  // Navigation State
  const [activeTab, setActiveTab] = useState<TabType>('profile');

  // Wizard State
  const { status: wizardStatus, loading: wizardLoading, refetch: refetchWizard } = useWizardStatus();
  const [selectedWizardStep, setSelectedWizardStep] = useState<number | null>(null);
  const [editingStep, setEditingStep] = useState<number | null>(null);

  // Initial Data Fetch & URL Param handling
  useEffect(() => {
    // Handle tab from URL query param
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab') as TabType;
    const step = params.get('step');
    if (tab && ['profile', 'email', 'email-config', 'billing', 'summarizer', 'wizard'].includes(tab)) {
      setActiveTab(tab);
      if (tab === 'wizard' && step) {
        setSelectedWizardStep(parseInt(step));
      }
    }
  }, []);

  const handleTabChange = () => {
    // Reset profile editing state when switching tabs
    if (isEditing) {
      handleCancel();
    } else {
      setError('');
      setSuccess('');
    }
    setEditingStep(null);
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-2 border-gray-300 border-t-gray-900"></div>
          <p className="mt-3 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Error State (if profile load failed)
  if (!profile && error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-5xl mx-auto">
          <Alert variant="error" message={error} />
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const role = profile.role?.toLowerCase?.();
  const isInternalUser = role === 'internal' || role === 'admin';

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto py-8 px-6">
          {/* Header */}
          <div className='flex flex-col md:flex-row'>
            <div className="mb-8 w-full md:w-64 flex-shrink-0 flex flex-col mr-8">
              <h1 className="text-2xl font-semibold text-gray-900">Account Settings</h1>
              <p className="text-sm text-gray-500 mt-1 mb-4">Manage your profile and account preferences</p>

              <ProfileSidebar
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                isInternalUser={isInternalUser}
                wizardData={wizardStatus?.data}
                onTabChange={handleTabChange}
                selectedWizardStep={selectedWizardStep}
                setSelectedWizardStep={setSelectedWizardStep}
              />
            </div>

            <div className="flex-1 pt-1">
              {/* Alert Messages (Global) */}
              {error && activeTab !== 'billing' && (
                <div className="mb-6">
                  <Alert variant="error" message={error} onDismiss={() => setError('')} />
                </div>
              )}
              {success && (
                <div className="mb-6">
                  <Alert variant="success" message={success} onDismiss={() => setSuccess('')} />
                </div>
              )}

              {/* Tab Content */}
              {activeTab === 'profile' && (
                <ProfileTab
                  profile={profile}
                  isEditing={isEditing}
                  onEdit={handleEdit}
                  onCancel={handleCancel}
                  onSave={() => handleSave()}
                  isSaving={isSaving}
                  formData={formData}
                  formErrors={formErrors}
                  onInputChange={handleInputChange}
                  onProfileUpdate={setProfile}
                />
              )}

              {activeTab === 'email' && (
                <EmailPasswordTab
                  profile={profile}
                  isEditing={isEditing}
                  onEdit={handleEdit}
                  onCancel={handleCancel}
                  onSave={() => handleSave()}
                  isSaving={isSaving}
                  formData={formData}
                  formErrors={formErrors}
                  onInputChange={handleInputChange}
                />
              )}

              {activeTab === 'email-config' && <EmailConfigTab />}

              {activeTab === 'billing' && (
                <BillingTab
                  initialFetch={true}
                  userStatus={profile.status}
                />
              )}

              {activeTab === 'summarizer' && <SummarizerTab />}

              {activeTab === 'wizard' && !isInternalUser && (
                <div className="space-y-6 mt-19">
                  {selectedWizardStep ? (
                    <WizardStepDetailView
                      stepNumber={selectedWizardStep}
                      wizardData={wizardStatus?.data}
                      onEdit={() => setEditingStep(selectedWizardStep)}
                      onSuccess={async () => {
                        await refetchWizard();
                      }}
                    />
                  ) : (
                    <WizardConfigView
                      wizardStatus={wizardStatus}
                      loading={wizardLoading}
                      onEditStep={(stepNumber) => setEditingStep(stepNumber)}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Wizard Step Edit Modal */}
      {editingStep !== null && (
        <WizardStepModal
          isOpen={editingStep !== null}
          onClose={() => setEditingStep(null)}
          stepNumber={editingStep}
          wizardData={wizardStatus?.data}
          onSuccess={async () => {
            await refetchWizard();
          }}
        />
      )
      }
    </>
  );
};

export default Profile;
